import DeviceInfo from 'react-native-device-info';
import { createReducer, createAction } from '@reduxjs/toolkit';
const signalR = require('@microsoft/signalr');

import api from '@api/index';
import { getToken, delay } from '@reducers/utils';
import { getSecureValue, isWeb, removeSecureValue, setSecureValue } from '@helpers';

import { onRequestForDeviceDetails, onRequestForNewDeviceConfirmation, onLogOffDevice, onAddedDevice } from '@reducers/device';
import { checkLogoutEvent } from '@reducers/user';
import { onChangedConnectorHealth, onResetedConnector } from '@reducers/connector';
import { getMachines, setDataToNCHashStore, onRefreshedMachineAddressList, onUpdatedMachineEventsTimeline } from '@reducers/machines';
import { machineStatusUpdatedMessageProducer } from '@reducers/machines/messageProducers/machineStatusUpdatedMessageProducer';
import { onRefreshedSearchData } from '@reducers/manufacture';
import { setFileConnectorData, onUploadedErrorLog } from '@reducers/fileConnector';
import { onFoundNewClampingTools } from '@reducers/clampingTools';
import { onFoundUnregisteredConnector, onOnboardedConnector } from '@reducers/onBoarding';
import { sendLog } from '@reducers/dataDogs';
import { buildSignalRConnectionLogTitle } from '@reducers/dataDogs/helpers';
import { addEventLogToHistory } from '@reducers/logs';

import {
  getConnectorIpRange,
  setSearchStatus,
  onFinishedRemoveTenant,
  onLoginAccountCreated,
  onLoginAccountCreationFailed,
} from '@reducers/membership';
import {
  onIgnoredProductionInformation,
  onRemovingPI,
  onSetChangeProductionInformation,
  onSetSetupProductionInformation,
  onAddedPI,
} from '@reducers/details';
import {
  removeImageDocumentMessageProducer,
  updateImageMetadataMessageProducer,
  rotateImageDocumentMessageProducer,
  cropImageDocumentMessageProducer,
  uploadImageDocumentMessageProducer,
} from '../v2/imageHandlingV2/messageProducers';
import {
  onRescheduledProductionOperations,
  onClosedProductionOperation,
  onFailedToCloseProductionOperation,
  onFailedToRescheduleProductionOperations,
} from '../v2/planboard';
import { handleFinishedRescheduling } from '@reducers/v2/planboard/thunks';

import type {
  SignalRState,
  SetConnectionIDPayload,
  SetReconnectingPayload,
  ConnectionPool,
  ConnectionConfig,
  EventSubscription,
  AdHocListener,
  BufferedEvent,
  DocumentChain,
  DocumentChainResult,
  InitSignalRParams,
  EventsPool,
} from './types';
import type { AppDispatch } from '@reducers/utils/types';
import { appVisibilityService } from '@services/appVisibility';

// Re-export types for consumers
export type { SignalRState, SetConnectionIDPayload, SetReconnectingPayload } from './types';

const initState: SignalRState = {
  showDeviceActivation: false,
  authenticationCode: '',
  expirationDateTime: '',
  deviceType: '',

  appHubID: null,
  signalRAnonymID: null,

  appHubReconnecting: false,
  signalRAnonymReconnecting: false,

  appHubInit: false,
  signalRAnonymInit: false,
};

export const setConnectionID = createAction<SetConnectionIDPayload>('signalR/setConnectionID');
export const setReconnecting = createAction<SetReconnectingPayload>('signalR/setReconnecting');
export const clear = createAction('clear');

const connectionPool: ConnectionPool = {};
const connectionPoolAlive: Record<string, ReturnType<typeof setInterval> | null> = {};
const connectionPoolErrorAlive: Record<string, ReturnType<typeof setTimeout> | null> = {};
const connectionStarting: Record<string, boolean> = {};
const connectionConfigs: Record<string, ConnectionConfig> = {};

const SIGNALR_RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 30000];

let connectionVisibilitySubscribed = false;

// Global event subscription management
const eventSubscriptions = new Map<string, Set<EventSubscription>>(); // eventName -> Set of { resolve, reject, matcher, timeoutId }

// Ad-hoc event listeners wired by registerEventSubscription for events not present in eventsPool.
// Keyed by connectionName -> eventName -> { handler, refcount }.
const adHocListeners = new Map<string, Map<string, AdHocListener>>();

// Document processing chain correlation management
const pendingDocumentChains = new Map<string, DocumentChain>(); // correlationKey -> chain
const pendingFileChains = new Map<string, string>(); // fileName -> correlationKey (for initial lookup by fileName)

// Event buffer management for handling out-of-order events
const eventBuffer = new Map<string, BufferedEvent>(); // eventKey -> { eventType, eventData, timestamp, processed }
const BUFFER_CLEANUP_INTERVAL = 30000; // 30 seconds
const BUFFER_MAX_AGE = 300000; // 5 minutes

// Event buffer cleanup timer
let bufferCleanupTimer: ReturnType<typeof setInterval> | null = null;

let bufferVisibilitySubscribed = false;

// Initialize buffer cleanup
const initializeBufferCleanup = (): void => {
  if (bufferCleanupTimer) {
    return;
  }

  const startCleanup = (): void => {
    if (bufferCleanupTimer) return;
    bufferCleanupTimer = setInterval(() => {
      cleanupEventBuffer();
    }, BUFFER_CLEANUP_INTERVAL);
  };

  const stopCleanup = (): void => {
    if (bufferCleanupTimer) {
      clearInterval(bufferCleanupTimer);
      bufferCleanupTimer = null;
    }
  };

  startCleanup();

  if (!bufferVisibilitySubscribed) {
    bufferVisibilitySubscribed = true;
    appVisibilityService.subscribe((isVisible) => {
      if (isVisible) {
        startCleanup();
      } else {
        stopCleanup();
      }
    });
  }
};

// Cleanup old buffered events
const cleanupEventBuffer = (): void => {
  const now = Date.now();
  let cleanedCount = 0;

  for (const [eventKey, bufferedEvent] of eventBuffer.entries()) {
    if (now - bufferedEvent.timestamp > BUFFER_MAX_AGE) {
      eventBuffer.delete(eventKey);
      cleanedCount++;
    }
  }

  if (cleanedCount > 0) {
    console.log(`[DocumentChain] Cleaned up ${cleanedCount} old buffered events. Remaining: ${eventBuffer.size}`);
  }
};

// Add event to buffer
const bufferEvent = (eventType: string, eventData: Record<string, unknown>): string | string[] | null => {
  const timestamp = Date.now();
  const eventKey = generateEventKey(eventType, eventData);

  if (!eventKey) {
    console.warn(`[DocumentChain] Cannot buffer event - unable to generate key for ${eventType}`, eventData);
    return null;
  }

  const bufferedEvent: BufferedEvent = {
    eventType,
    eventData,
    timestamp,
    processed: false,
  };

  if (Array.isArray(eventKey)) {
    eventKey.forEach((key) => {
      eventBuffer.set(key, { ...bufferedEvent });
    });
  } else {
    eventBuffer.set(eventKey, bufferedEvent);
  }
  console.log(`[DocumentChain] Buffered ${eventType} event with key: ${eventKey}. Buffer size: ${eventBuffer.size}`);

  // Initialize cleanup if not already running
  initializeBufferCleanup();

  return eventKey;
};

// Generate unique event key for buffering
const generateEventKey = (eventType: string, eventData: Record<string, unknown>): string | string[] | null => {
  try {
    switch (eventType) {
      case 'OnStartedDocumentProcess':
        return eventData?.fileName && eventData?.documentBlobId ? `start-${eventData.fileName}-${eventData.documentBlobId}` : null;

      case 'OnProcessedNcFileDocument': {
        const ncFile = eventData?.ncFile as Record<string, unknown> | undefined;
        return eventData?.documentBlobId && ncFile?.ncFileId ? `processed-${eventData.documentBlobId}-${ncFile.ncFileId}` : null;
      }

      case 'OnAddedProductionInformation': {
        const ncSmartLinks = eventData?.ncSmartLinks as Array<Record<string, unknown>> | undefined;
        if (Array.isArray(ncSmartLinks) && ncSmartLinks.length > 0) {
          // Create keys for each link in the event
          const keys = ncSmartLinks
            .filter((link) => link.ncFileId && link.productionOperationId)
            .map((link) => `production-${link.ncFileId}-${link.productionOperationId}`);
          return keys.length > 0 ? keys : null;
        }
        return null;
      }

      default:
        return null;
    }
  } catch (error) {
    console.error(`[DocumentChain] Error generating event key for ${eventType}:`, error);
    return null;
  }
};

// Process buffered events for a chain
const processBufferedEventsForChain = (correlationKey: string, chain: DocumentChain): void => {
  if (!chain) {
    return;
  }

  console.log(`[DocumentChain] Processing buffered events for chain ${correlationKey}`, {
    fileName: chain.fileName,
    documentBlobId: chain.documentBlobId,
    ncFileId: chain.ncFileId,
    productionOperationId: chain.productionOperationId,
  });

  let processedCount = 0;

  for (const [eventKey, bufferedEvent] of eventBuffer.entries()) {
    if (bufferedEvent.processed) {
      continue;
    }

    const { eventType, eventData } = bufferedEvent;
    let shouldProcess = false;

    try {
      switch (eventType) {
        case 'OnStartedDocumentProcess':
          // Match by fileName if documentBlobId is not set yet
          if (!chain.documentBlobId && eventData?.fileName === chain.fileName) {
            shouldProcess = true;
          }
          // Match by documentBlobId if it's set
          else if (chain.documentBlobId && eventData?.documentBlobId === chain.documentBlobId) {
            shouldProcess = true;
          }
          break;

        case 'OnProcessedNcFileDocument': {
          const ncFile = eventData?.ncFile as Record<string, unknown> | undefined;
          // Match by documentBlobId if available
          if (chain.documentBlobId && eventData?.documentBlobId === chain.documentBlobId) {
            shouldProcess = true;
          }
          // Try to match by fileName if documentBlobId is not set yet
          else if (!chain.documentBlobId && ncFile?.ncFileName === chain.fileName) {
            shouldProcess = true;
          }
          break;
        }

        case 'OnAddedProductionInformation': {
          const ncSmartLinks = eventData?.ncSmartLinks as Array<Record<string, unknown>> | undefined;
          // Match by ncFileId and productionOperationId
          if (chain.ncFileId && Array.isArray(ncSmartLinks)) {
            const hasMatchingLink = ncSmartLinks.some(
              (link) => link.ncFileId == chain.ncFileId && link.productionOperationId == chain.productionOperationId
            );
            if (hasMatchingLink) {
              shouldProcess = true;
            }
          }
          break;
        }
      }

      if (shouldProcess && !chain.collectedEvents[getEventCollectionKey(eventType) as string]) {
        console.log(`[DocumentChain] Processing buffered ${eventType} for chain ${correlationKey}`);

        // Process the buffered event
        processEventForChain(correlationKey, chain, eventType, eventData);

        // Mark as processed
        bufferedEvent.processed = true;
        processedCount++;
      }
    } catch (error) {
      console.error(`[DocumentChain] Error processing buffered event ${eventType} for chain ${correlationKey}:`, error);
    }
  }

  if (processedCount > 0) {
    console.log(`[DocumentChain] Processed ${processedCount} buffered events for chain ${correlationKey}`);
  }
};

// Get collection key for event type
const getEventCollectionKey = (eventType: string): string | null => {
  switch (eventType) {
    case 'OnStartedDocumentProcess':
      return 'startedDocumentProcess';
    case 'OnProcessedNcFileDocument':
      return 'processedDocument';
    case 'OnAddedProductionInformation':
      return 'addedProductionInfo';
    default:
      return null;
  }
};

// Process an event for a specific chain
const processEventForChain = (correlationKey: string, chain: DocumentChain, eventType: string, eventData: Record<string, unknown>): void => {
  const collectionKey = getEventCollectionKey(eventType);
  if (!collectionKey) {
    return;
  }

  // Store the event
  chain.collectedEvents[collectionKey] = eventData;

  // Update chain properties based on event type
  switch (eventType) {
    case 'OnStartedDocumentProcess':
      if (eventData?.documentBlobId) {
        chain.documentBlobId = eventData.documentBlobId as string;
        console.log(`[DocumentChain] Set documentBlobId: ${eventData.documentBlobId} for chain ${correlationKey}`);
      }
      break;

    case 'OnProcessedNcFileDocument': {
      const ncFile = eventData?.ncFile as Record<string, unknown> | undefined;
      if (ncFile?.ncFileId) {
        chain.ncFileId = ncFile.ncFileId as string;
        console.log(`[DocumentChain] Set ncFileId: ${ncFile.ncFileId} for chain ${correlationKey}`);
      }
      if (eventData?.documentBlobId && !chain.documentBlobId) {
        chain.documentBlobId = eventData.documentBlobId as string;
        console.log(`[DocumentChain] Set documentBlobId: ${eventData.documentBlobId} from processed event for chain ${correlationKey}`);
      }
      break;
    }
  }

  // Check for completion after processing
  checkChainCompletionByKey(correlationKey);

  // Process any remaining buffered events now that chain properties might have changed
  if (eventType === 'OnStartedDocumentProcess' || eventType === 'OnProcessedNcFileDocument') {
    // Delay slightly to allow this event to complete processing first
    setTimeout(() => {
      processBufferedEventsForChain(correlationKey, chain);
    }, 10);
  }
};

// Try to match buffered events with existing chains
const processBufferedEvents = (): void => {
  for (const [correlationKey, chain] of pendingDocumentChains.entries()) {
    processBufferedEventsForChain(correlationKey, chain);
  }
};

// Is the event wired by eventsPool for this connection? If so, the pool handler already
// calls notifyEventSubscribers and we MUST NOT register a second listener (would double-fire).
const isWiredByEventsPool = (connectionName: string, eventName: string): boolean => {
  const events = eventsPool[connectionName];
  return !!events && eventName in events;
};

// Ensure connection.on(eventName, ...) is wired for events not in eventsPool.
// Refcounted so multiple concurrent subscribers share a single listener.
const ensureEventListener = (connectionName: string, eventName: string): void => {
  if (isWiredByEventsPool(connectionName, eventName)) return;

  let perConnection = adHocListeners.get(connectionName);
  if (!perConnection) {
    perConnection = new Map();
    adHocListeners.set(connectionName, perConnection);
  }

  const existing = perConnection.get(eventName);
  if (existing) {
    existing.refcount++;
    return;
  }

  const handler = (e: unknown) => {
    try {
      notifyEventSubscribers(eventName, e);
    } catch (err) {
      console.log(`Error in ad-hoc listener for ${eventName}`, err);
    }
  };
  perConnection.set(eventName, { handler, refcount: 1 });

  const connection = connectionPool[connectionName];
  if (connection) {
    connection.on(eventName, handler);
  }
};

const releaseEventListener = (connectionName: string, eventName: string): void => {
  if (isWiredByEventsPool(connectionName, eventName)) return;
  const perConnection = adHocListeners.get(connectionName);
  if (!perConnection) return;
  const entry = perConnection.get(eventName);
  if (!entry) return;
  entry.refcount--;
  if (entry.refcount > 0) return;
  const connection = connectionPool[connectionName];
  if (connection) {
    connection.off(eventName, entry.handler);
  }
  perConnection.delete(eventName);
  if (perConnection.size === 0) adHocListeners.delete(connectionName);
};

// Replay tracked ad-hoc listeners against a freshly-built connection (after close + re-init).
const attachAdHocListenersToConnection = (connectionName: string): void => {
  const perConnection = adHocListeners.get(connectionName);
  if (!perConnection) return;
  const connection = connectionPool[connectionName];
  if (!connection) return;
  perConnection.forEach((entry, eventName) => {
    connection.on(eventName, entry.handler);
  });
};

// Function to register an event subscription
export const registerEventSubscription = (
  eventName: string,
  resolve: (value: unknown) => void,
  reject: (reason?: unknown) => void,
  matcher?: ((eventData: unknown) => boolean) | null,
  timeout?: number,
  connectionName: string = 'appHub'
): (() => void) => {
  if (!eventSubscriptions.has(eventName)) {
    eventSubscriptions.set(eventName, new Set());
  }

  let released = false;
  const releaseOnce = () => {
    if (released) return;
    released = true;
    releaseEventListener(connectionName, eventName);
  };

  const subscription: EventSubscription = { resolve, reject, matcher, release: releaseOnce };

  // Add timeout handling
  if (timeout && timeout > 0) {
    subscription.timeoutId = setTimeout(() => {
      eventSubscriptions.get(eventName)?.delete(subscription);
      releaseOnce();
      reject(new Error(`Event '${eventName}' timeout after ${timeout}ms`));
    }, timeout);
  }

  eventSubscriptions.get(eventName)!.add(subscription);
  ensureEventListener(connectionName, eventName);

  // Return cleanup function
  return () => {
    const subscriptions = eventSubscriptions.get(eventName);
    if (subscriptions) {
      if (subscription.timeoutId) {
        clearTimeout(subscription.timeoutId);
      }
      subscriptions.delete(subscription);
      if (subscriptions.size === 0) {
        eventSubscriptions.delete(eventName);
      }
    }
    releaseOnce();
  };
};

// Function to notify event subscribers
const notifyEventSubscribers = (eventName: string, eventData: unknown): void => {
  const subscriptions = eventSubscriptions.get(eventName);
  if (!subscriptions || subscriptions.size === 0) {
    return;
  }

  // Create a copy to avoid issues with concurrent modification
  const subscriptionsCopy = Array.from(subscriptions);

  subscriptionsCopy.forEach((subscription) => {
    const { resolve, reject, matcher, timeoutId, release } = subscription;

    try {
      // Check if custom matcher is satisfied
      if (matcher && !matcher(eventData)) {
        return; // Don't resolve yet, wait for matching event
      }

      // Clean up timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Remove from subscriptions
      subscriptions.delete(subscription);
      release?.();

      // Resolve the promise
      resolve(eventData);
    } catch (error) {
      // Clean up timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Remove from subscriptions
      subscriptions.delete(subscription);
      release?.();

      // Reject the promise
      reject(error);
    }
  });

  // Clean up empty subscription sets
  if (subscriptions.size === 0) {
    eventSubscriptions.delete(eventName);
  }
};

// Clear all pending event subscriptions (called on disconnect to prevent closure accumulation)
const clearAllEventSubscriptions = (): void => {
  for (const [, subscriptions] of eventSubscriptions.entries()) {
    for (const subscription of subscriptions) {
      if (subscription.timeoutId) {
        clearTimeout(subscription.timeoutId);
      }
      subscription.release?.();
      subscription.reject(new Error('SignalR connection closed - subscription cleared'));
    }
    subscriptions.clear();
  }
  eventSubscriptions.clear();
};

// Clear all pending document chains (called on disconnect)
const clearAllDocumentChains = (): void => {
  for (const [, chain] of pendingDocumentChains.entries()) {
    if (chain.timeoutId) {
      clearTimeout(chain.timeoutId);
    }
    chain.reject(new Error('SignalR connection closed - document chain cleared'));
  }
  pendingDocumentChains.clear();
  pendingFileChains.clear();
  eventBuffer.clear();
};

const initializeConnectionVisibilityCheck = (): void => {
  if (connectionVisibilitySubscribed) {
    return;
  }
  connectionVisibilitySubscribed = true;

  appVisibilityService.subscribe((isVisible) => {
    if (!isVisible) {
      return;
    }
    Object.keys(connectionPool).forEach((name) => {
      const c = connectionPool[name];
      const cfg = connectionConfigs[name];
      if (!c || !cfg) {
        return;
      }
      const state = c.state;
      const isHealthy =
        state === signalR.HubConnectionState.Connected ||
        state === signalR.HubConnectionState.Connecting ||
        state === signalR.HubConnectionState.Reconnecting;
      if (isHealthy) {
        return;
      }
      cfg.dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
      cfg.dispatch(
        sendLog({
          title: buildSignalRConnectionLogTitle(name, 'VisibilityResume'),
          data: { hub: name, state, reason: 'connection not healthy on foreground' },
          status: 'warn',
        })
      );
      connectionPool[name] = null;
      if (connectionPoolAlive[name]) {
        clearInterval(connectionPoolAlive[name]!);
        connectionPoolAlive[name] = null;
      }
      initConnection(cfg);
    });
  });
};

// Document processing chain correlation functions
export const registerDocumentChainByFileName = (
  fileName: string,
  productionOperationId: string,
  resolve: (value: unknown) => void,
  reject: (reason?: unknown) => void,
  timeout: number
): (() => void) | null => {
  console.log(`[DocumentChain] Registering chain for fileName: ${fileName}, productionOperationId: ${productionOperationId}`);

  if (pendingFileChains.has(fileName)) {
    const existingCorrelationKey = pendingFileChains.get(fileName);
    console.warn(`[DocumentChain] Chain already exists for fileName: ${fileName}`, existingCorrelationKey);
    reject(new Error(`Document chain already exists for fileName: ${fileName}`));
    return null;
  }

  const correlationKey = `${fileName}-${Date.now()}-${Math.random()}`;

  const chain: DocumentChain = {
    fileName,
    documentBlobId: null,
    productionOperationId,
    resolve,
    reject,
    collectedEvents: {},
    startTime: Date.now(),
    timeoutId: null,
    ncFileId: null,
  };

  // Set up timeout
  if (timeout > 0) {
    chain.timeoutId = setTimeout(() => {
      console.log(`[DocumentChain] Timeout for fileName: ${fileName} after ${timeout}ms`);
      pendingDocumentChains.delete(correlationKey);
      pendingFileChains.delete(fileName);
      reject(new Error(`Document processing chain timeout after ${timeout}ms for fileName: ${fileName}`));
    }, timeout);
  }

  pendingDocumentChains.set(correlationKey, chain);
  pendingFileChains.set(fileName, correlationKey);
  console.log(`[DocumentChain] Chain registered successfully. Pending chains count: ${pendingDocumentChains.size}`);

  // Process any buffered events that might match this new chain
  console.log(`[DocumentChain] Checking for buffered events for newly registered chain: ${correlationKey}`);
  setTimeout(() => {
    processBufferedEventsForChain(correlationKey, chain);
  }, 50); // Small delay to ensure chain is fully registered

  // Return cleanup function
  return () => {
    const chainToClean = pendingDocumentChains.get(correlationKey);
    if (chainToClean) {
      if (chainToClean.timeoutId) {
        clearTimeout(chainToClean.timeoutId);
      }
      pendingDocumentChains.delete(correlationKey);
      pendingFileChains.delete(fileName);
      console.log(`[DocumentChain] Cleaned up chain for fileName: ${fileName}`);
    }
  };
};

// Handle OnStartedDocumentProcess event
const handleStartedDocumentProcessEvent = (eventData: Record<string, unknown>): void => {
  try {
    console.log(`[DocumentChain] Processing OnStartedDocumentProcess event`, eventData);

    const fileName = eventData?.fileName as string | undefined;
    const documentBlobId = eventData?.documentBlobId as string | undefined;
    const documentType = eventData?.documentType as string | undefined;
    const links = eventData?.links as Array<Record<string, unknown>> | undefined;

    if (!fileName || !documentBlobId) {
      console.warn(`[DocumentChain] Invalid OnStartedDocumentProcess event data`, { fileName, documentBlobId, eventData });
      return;
    }

    console.log(`[DocumentChain] Found fileName: ${fileName}, documentBlobId: ${documentBlobId}, documentType: ${documentType}`);

    // Check if we have a pending chain for this fileName
    const correlationKey = pendingFileChains.get(fileName);
    if (!correlationKey) {
      console.log(
        `[DocumentChain] No pending chain found for fileName: ${fileName}. Buffering event. Current pending file chains:`,
        Array.from(pendingFileChains.keys())
      );
      // Buffer the event for later processing
      bufferEvent('OnStartedDocumentProcess', eventData);
      return;
    }

    const chain = pendingDocumentChains.get(correlationKey);
    if (!chain) {
      console.warn(`[DocumentChain] Chain not found for correlationKey: ${correlationKey}, buffering event`);
      bufferEvent('OnStartedDocumentProcess', eventData);
      return;
    }

    console.log(`[DocumentChain] Found matching chain for fileName: ${fileName}. Setting documentBlobId: ${documentBlobId}`);

    // Process the event using the unified processing function
    processEventForChain(correlationKey, chain, 'OnStartedDocumentProcess', eventData);

    // Extract productionOperationId from links if available
    if (Array.isArray(links) && links.length > 0) {
      const matchingLink = links.find((link) => link.scopeReferenceId == chain.productionOperationId);
      if (matchingLink) {
        console.log(`[DocumentChain] Found matching link in OnStartedDocumentProcess`, matchingLink);
      }
    }
  } catch (error) {
    console.error(`[DocumentChain] Error handling OnStartedDocumentProcess event:`, error, eventData);
  }
};

// Handle OnProcessedNcFileDocument event
const handleProcessedDocumentEvent = (eventData: Record<string, unknown>): void => {
  try {
    console.log(`[DocumentChain] Processing OnProcessedNcFileDocument event`, eventData);

    const ncFile = eventData?.ncFile as Record<string, unknown> | undefined;
    const documentBlobId = eventData?.documentBlobId as string | undefined;
    const ncFileId = ncFile?.ncFileId as string | undefined;
    const ncFileName = ncFile?.ncFileName as string | undefined;

    if (!documentBlobId || !ncFileId) {
      console.warn(`[DocumentChain] Invalid OnProcessedNcFileDocument event data`, { documentBlobId, ncFileId, eventData });
      return;
    }

    console.log(`[DocumentChain] Found documentBlobId: ${documentBlobId}, ncFileId: ${ncFileId}, ncFileName: ${ncFileName}`);

    // Find chain by documentBlobId first
    let matchingCorrelationKey: string | null = null;
    let matchingChain: DocumentChain | null = null;

    for (const [correlationKey, chain] of pendingDocumentChains.entries()) {
      if (chain.documentBlobId === documentBlobId) {
        matchingCorrelationKey = correlationKey;
        matchingChain = chain;
        break;
      }
    }

    // If no match by documentBlobId, try to match by fileName
    if (!matchingChain && ncFileName) {
      const correlationKey = pendingFileChains.get(ncFileName);
      if (correlationKey) {
        const chain = pendingDocumentChains.get(correlationKey);
        if (chain && !chain.collectedEvents.processedDocument) {
          matchingCorrelationKey = correlationKey;
          matchingChain = chain;
          console.log(`[DocumentChain] Found matching chain by fileName: ${ncFileName} for documentBlobId: ${documentBlobId}`);
        }
      }
    }

    if (!matchingChain) {
      console.log(
        `[DocumentChain] No pending chain found for documentBlobId: ${documentBlobId} or fileName: ${ncFileName}. Buffering event. Current pending chains:`,
        Array.from(pendingDocumentChains.entries()).map(([key, chain]) => ({
          key,
          fileName: chain.fileName,
          documentBlobId: chain.documentBlobId,
        }))
      );
      // Buffer the event for later processing
      bufferEvent('OnProcessedNcFileDocument', eventData);
      return;
    }

    console.log(`[DocumentChain] Found matching chain for documentBlobId: ${documentBlobId}. Setting ncFileId: ${ncFileId}`);

    // Process the event using the unified processing function
    processEventForChain(matchingCorrelationKey!, matchingChain, 'OnProcessedNcFileDocument', eventData);
  } catch (error) {
    console.error(`[DocumentChain] Error handling OnProcessedNcFileDocument event:`, error, eventData);
  }
};

// Handle OnAddedProductionInformation event
const handleAddedProductionInfoEvent = (eventData: Record<string, unknown>): void => {
  try {
    console.log(`[DocumentChain] Processing OnAddedProductionInformation event`, eventData);

    const ncSmartLinks = eventData?.ncSmartLinks as Array<Record<string, unknown>> | undefined;

    if (!Array.isArray(ncSmartLinks) || ncSmartLinks.length === 0) {
      console.warn(`[DocumentChain] Invalid OnAddedProductionInformation event data - no ncSmartLinks`, eventData);
      return;
    }

    console.log(`[DocumentChain] Found ${ncSmartLinks.length} ncSmartLinks`, ncSmartLinks);

    let foundMatch = false;

    // Check all pending chains to see if any match this event
    for (const [correlationKey, chain] of pendingDocumentChains.entries()) {
      // Skip chains that don't have ncFileId yet or already have production info
      if (!chain.ncFileId || chain.collectedEvents.addedProductionInfo) {
        continue;
      }

      // Check if any ncSmartLink matches our criteria
      const matchingLink = ncSmartLinks.find((link) => link.ncFileId == chain.ncFileId && link.productionOperationId == chain.productionOperationId);

      if (matchingLink) {
        console.log(`[DocumentChain] Found matching production info for correlationKey: ${correlationKey}`, {
          fileName: chain.fileName,
          documentBlobId: chain.documentBlobId,
          ncFileId: chain.ncFileId,
          productionOperationId: chain.productionOperationId,
          matchingLink,
        });

        // Process the event using the unified processing function
        processEventForChain(correlationKey, chain, 'OnAddedProductionInformation', eventData);

        foundMatch = true;
      }
    }

    if (!foundMatch) {
      console.log(
        `[DocumentChain] No matching chain found for production info event with links. Buffering event.`,
        ncSmartLinks.map((link) => ({ ncFileId: link.ncFileId, productionOperationId: link.productionOperationId }))
      );
      // Buffer the event for later processing
      bufferEvent('OnAddedProductionInformation', eventData);
    }
  } catch (error) {
    console.error(`[DocumentChain] Error handling OnAddedProductionInformation event:`, error, eventData);
  }
};

// Check if a chain is complete and resolve if so (by correlation key)
const checkChainCompletionByKey = (correlationKey: string): void => {
  const chain = pendingDocumentChains.get(correlationKey);
  if (!chain) {
    console.warn(`[DocumentChain] Chain not found for completion check: ${correlationKey}`);
    return;
  }

  const hasStartedProcess = !!chain.collectedEvents.startedDocumentProcess;
  const hasProcessedDocument = !!chain.collectedEvents.processedDocument;
  const hasProductionInfo = !!chain.collectedEvents.addedProductionInfo;

  console.log(`[DocumentChain] Completion check for ${correlationKey}:`, {
    fileName: chain.fileName,
    documentBlobId: chain.documentBlobId,
    hasStartedProcess,
    hasProcessedDocument,
    hasProductionInfo,
    ncFileId: chain.ncFileId,
  });

  if (hasStartedProcess && hasProcessedDocument && hasProductionInfo) {
    console.log(`[DocumentChain] Chain complete! Resolving for correlationKey: ${correlationKey}`);

    // Clean up timeout
    if (chain.timeoutId) {
      clearTimeout(chain.timeoutId);
    }

    // Resolve with all three events
    const result: DocumentChainResult = {
      startedDocumentProcess: chain.collectedEvents.startedDocumentProcess,
      processedDocument: chain.collectedEvents.processedDocument,
      addedProductionInfo: chain.collectedEvents.addedProductionInfo,
      correlationData: {
        fileName: chain.fileName,
        documentBlobId: chain.documentBlobId,
        ncFileId: chain.ncFileId,
        productionOperationId: chain.productionOperationId,
        duration: Date.now() - chain.startTime,
      },
    };

    // Remove from pending chains
    pendingDocumentChains.delete(correlationKey);
    if (chain.fileName) {
      pendingFileChains.delete(chain.fileName);
    }

    console.log(`[DocumentChain] Chain resolved successfully. Remaining pending chains: ${pendingDocumentChains.size}`);

    chain.resolve(result);
  } else {
    console.log(`[DocumentChain] Chain not yet complete for ${correlationKey}. Waiting for missing events.`);
  }
};

// Legacy function for backward compatibility
const checkChainCompletion = (documentBlobId: string): void => {
  // Find correlation key by documentBlobId
  for (const [correlationKey, chain] of pendingDocumentChains.entries()) {
    if (chain.documentBlobId === documentBlobId) {
      return checkChainCompletionByKey(correlationKey);
    }
  }
  console.warn(`[DocumentChain] Chain not found for completion check by documentBlobId: ${documentBlobId}`);
};

const ignoreLogsList = ['OnRefreshedSearchData', 'OnChangedNcFile', 'OnProcessedOtherDocument', 'OnUpdatedMachineStatus'];

const eventsPool: EventsPool = {
  appHub: {
    OnChangedNcFile: setDataToNCHashStore,
    OnFailedToChangeProductionInformation: getMachines,
    OnMachineConfigurationChange: getMachines,
    OnFailedMachineConfigurationChange: getMachines,
    OnRequestForFullMachineConfigurationChange: getMachines,
    OnUpdatedMachineStatus: machineStatusUpdatedMessageProducer,
    OnFoundNewClampingTools: onFoundNewClampingTools,
    OnChangedMachineConnection: getMachines,
    OnUpdatedDocumentMetadata: updateImageMetadataMessageProducer,
    OnRotatedImage: rotateImageDocumentMessageProducer,
    // OnStartedDocumentProcess: () => {},
    // OnStartedCroppingImage: () => {},
    // OnStartedRotatingImage: () => {},
    OnCroppedImage: cropImageDocumentMessageProducer,
    OnRemovedImage: removeImageDocumentMessageProducer,
    OnProcessedImageDocument: uploadImageDocumentMessageProducer,
    // OnProcessedNcFileDocument: () => {},
    OnProcessedOtherDocument: uploadImageDocumentMessageProducer,
    OnRefreshedMachineAddressList: onRefreshedMachineAddressList,
    OnUpdatedMachineEventsTimeline: onUpdatedMachineEventsTimeline,
    OnAddedProductionInformation: onAddedPI,
    OnUploadedErrorLog: onUploadedErrorLog,
    OnRefreshedFileUploadStatistics: setFileConnectorData,
    OnRefreshedSearchData: onRefreshedSearchData,
    // OnFoundMachineAddress: saveMachineAddress,
    OnChangedConnectorHealth: onChangedConnectorHealth,
    OnResetedConnector: onResetedConnector,
    OnFinishedRemoveTenant: onFinishedRemoveTenant,
    OnLoginAccountCreated: onLoginAccountCreated,
    OnLoginAccountCreationFailed: onLoginAccountCreationFailed,
    OnRequestForDeviceDetails: onRequestForDeviceDetails,
    OnRequestForNewDeviceConfirmation: onRequestForNewDeviceConfirmation,
    OnLogOffDevice: onLogOffDevice,
    OnAddedDevice: onAddedDevice,
    OnUpdatedIpScanStatus: setSearchStatus,
    OnLogOffUser: checkLogoutEvent,
    OnConfigureIpRangeForScanRequest: getConnectorIpRange,
    OnRemoveIpRangeFromScanRequest: getConnectorIpRange,
    OnSetChangeProductionInformation: onSetChangeProductionInformation,
    OnSetSetupProductionInformation: onSetSetupProductionInformation,
    OnIgnoredProductionInformation: onIgnoredProductionInformation,
    OnRemovedProductionInformation: onRemovingPI,
    OnRescheduledProductionOperations: onRescheduledProductionOperations,
    OnClosedProductionOperation: onClosedProductionOperation,
    OnFailedToCloseProductionOperation: onFailedToCloseProductionOperation,
    OnFailedToRescheduleProductionOperations: onFailedToRescheduleProductionOperations,
    OnFinishedReschedulingProductionOperations: handleFinishedRescheduling,
  },
  signalRAnonym: {
    // OnAuthenticatedDevice: onAuthenticatedDevice,
    OnFoundUnregisteredConnector: onFoundUnregisteredConnector,
    OnOnboardedConnector: onOnboardedConnector,
  },
};

export const getConnection = (): ConnectionPool => connectionPool;

const setHeaders = async (config: Record<string, unknown>): Promise<void> => {
  if (!isWeb) {
    (config as Record<string, Record<string, string>>).headers = {
      'X-Device-ID': await DeviceInfo.getUniqueId(),
      'X-Device-Type': 'App',
    };
  } else {
    const getUniqueId = getSecureValue('getUniqueId');
    if (getUniqueId) {
      (config as Record<string, Record<string, string>>).headers = {
        'X-Device-ID': getUniqueId as string,
        'X-Device-Type': 'App',
      };
    }
  }
};

export const anonymSignalR = (callback: () => void) => async (dispatch: AppDispatch) => {
  const { signalRMembershipOnboardingURL } = await api();

  const headers: Record<string, unknown> = {};

  await setHeaders(headers);

  initConnection({
    url: signalRMembershipOnboardingURL,
    name: 'signalRAnonym',
    dispatch,
    headers,
    callbacks: {
      signalRAnonym: callback,
    },
  });
};

export const closeAnonymSignalR = () => async (dispatch: AppDispatch) => {
  dispatch(closeConnection('signalRAnonym'));
};

export const closeConnections = () => async (dispatch: AppDispatch) => {
  Object.keys(connectionPool).map((name) => dispatch(closeConnection(name)));
};

export const closeConnection = (name: string) => async (dispatch: AppDispatch) => {
  if (connectionPoolAlive[name]) {
    clearInterval(connectionPoolAlive[name]!);
    connectionPoolAlive[name] = null;
  }

  if (connectionPoolErrorAlive[name]) {
    clearTimeout(connectionPoolErrorAlive[name]!);
    connectionPoolErrorAlive[name] = null;
  }

  connectionStarting[name] = false;
  delete connectionConfigs[name];
  dispatch(setReconnecting({ name: `${name}Reconnecting`, value: false }));

  if (connectionPool[name]) {
    const stoppedConnectionId = connectionPool[name]!.connectionId;
    removeSecureValue(`${name}ID`);
    dispatch(setConnectionID({ name: `${name}ID`, id: null }));
    dispatch(addEventLogToHistory('SignalR:ConnectionStopped', { hub: name, timestamp: new Date().toISOString() }));
    dispatch(
      sendLog({
        title: buildSignalRConnectionLogTitle(name, 'Stopped'),
        data: { hub: name, connectionId: stoppedConnectionId, reason: 'explicit closeConnection call' },
        status: 'debug',
      })
    );
    connectionPool[name]!.stop();
    connectionPool[name] = null;
  }
};

export const initSignalR =
  (params: InitSignalRParams = {}) =>
  async (dispatch: AppDispatch) => {
    const { callbacks = {} } = params;
    const { appHubURL, signalRMembershipOnboardingURL } = await api();

    initConnection({
      url: appHubURL,
      name: 'appHub',
      dispatch,
      callbacks,
    });

    initConnection({
      url: signalRMembershipOnboardingURL,
      name: 'signalRAnonym',
      dispatch,
      callbacks,
    });
  };

const initConnection = async (config: ConnectionConfig): Promise<void> => {
  const { dispatch, url, headers, name, callbacks } = config;
  if (connectionPool[name] || connectionStarting[name]) {
    if (callbacks?.[name]) {
      callbacks[name]!();
    }
    return;
  }
  connectionStarting[name] = true;
  connectionConfigs[name] = config;
  initializeConnectionVisibilityCheck();

  try {
    dispatch(setReconnecting({ name: `${name}Init`, value: true }));
    dispatch(addEventLogToHistory('SignalR:InitializingConnection', { hub: name, url, timestamp: new Date().toISOString() }));
    dispatch(
      sendLog({
        title: buildSignalRConnectionLogTitle(name, 'Initializing'),
        data: { hub: name, url },
        status: 'debug',
      })
    );

    let connectionHeaders = headers;
    if (!connectionHeaders) {
      let resolvedToken: string | null = null;
      while (!resolvedToken) {
        const { token, tokenUpdating } = await getToken();
        if (tokenUpdating) {
          await delay(500);
          continue;
        }
        if (!token) {
          await delay(1000);
          continue;
        }
        resolvedToken = token;
      }

      connectionHeaders = {
        accessTokenFactory: () => getSecureValue('token') || resolvedToken!,
      };

      await setHeaders(connectionHeaders);
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, { ...connectionHeaders, ignoreInterceptor: 'true' })
      .withAutomaticReconnect(SIGNALR_RECONNECT_DELAYS_MS)
      .build();

    connection.onreconnecting((error?: Error) => {
      dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
      dispatch(addEventLogToHistory('SignalR:AutoReconnecting', { hub: name, timestamp: new Date().toISOString() }));
      dispatch(
        sendLog({
          title: buildSignalRConnectionLogTitle(name, 'AutoReconnecting'),
          data: { hub: name, error: error ? error.message || String(error) : null },
          status: 'warn',
        })
      );
    });

    connection.onreconnected((connectionId?: string) => {
      if (connectionId) {
        setSecureValue(`${name}ID`, connectionId);
        dispatch(setConnectionID({ name: `${name}ID`, id: connectionId }));
      }
      dispatch(setReconnecting({ name: `${name}Reconnecting`, value: false }));
      dispatch(
        addEventLogToHistory('SignalR:AutoReconnected', { hub: name, connectionId: connectionId ?? null, timestamp: new Date().toISOString() })
      );
      dispatch(
        sendLog({
          title: buildSignalRConnectionLogTitle(name, 'AutoReconnected'),
          data: { hub: name, connectionId: connectionId ?? null },
          status: 'debug',
        })
      );
    });

    connection.onclose(async (error?: Error) => {
      const closedConnectionId = connectionPool[name]?.connectionId ?? null;
      removeSecureValue(`${name}ID`);
      dispatch(setConnectionID({ name: `${name}ID`, id: null }));
      dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
      console.log('Close - ', name);
      dispatch(addEventLogToHistory('SignalR:ConnectionClosed', { hub: name, timestamp: new Date().toISOString() }));
      dispatch(
        sendLog({
          title: buildSignalRConnectionLogTitle(name, 'Closed'),
          data: {
            hub: name,
            connectionId: closedConnectionId,
            error: error ? error.message || String(error) : null,
          },
          status: error ? 'error' : 'warn',
        })
      );
      // Clean up stale subscriptions and chains to prevent closure accumulation
      clearAllEventSubscriptions();
      clearAllDocumentChains();
      adHocListeners.delete(name);

      connectionPool[name] = null;
    });

    const events = eventsPool[name];

    Object.keys(events).map((eventName) => {
      connection.on(eventName, (e: Record<string, unknown>) => {
        if (!ignoreLogsList.includes(eventName)) {
          console.log(eventName, e);
        }
        dispatch(addEventLogToHistory(`SignalR:Event:${eventName}`, { hub: name, data: e, timestamp: new Date().toISOString() }));
        try {
          // Execute existing event handler
          dispatch(events[eventName](e));
        } catch (err) {
          console.log(`Error when call event pool ${eventName}`, err);
        }
        try {
          // Notify any waiting event subscribers
          notifyEventSubscribers(eventName, e);
        } catch (err) {
          console.log(`Error when call notifyEventSubscribers ${eventName}`, err);
        }

        // Handle document processing chain correlation
        if (eventName === 'OnStartedDocumentProcess') {
          handleStartedDocumentProcessEvent(e);
        } else if (eventName === 'OnProcessedNcFileDocument') {
          handleProcessedDocumentEvent(e);
        } else if (eventName === 'OnAddedProductionInformation') {
          handleAddedProductionInfoEvent(e);
        }
      });
    });

    connection
      .start()
      .then(async () => {
        connectionStarting[name] = false;
        connectionPool[name] = connection;
        attachAdHocListenersToConnection(name);
        console.log({ name: `${name}ID`, id: connectionPool[name]!.connectionId });
        dispatch(
          addEventLogToHistory('SignalR:ConnectionEstablished', {
            hub: name,
            connectionId: connectionPool[name]!.connectionId,
            timestamp: new Date().toISOString(),
          })
        );
        dispatch(
          sendLog({
            title: buildSignalRConnectionLogTitle(name, 'Established'),
            data: { hub: name, connectionId: connectionPool[name]!.connectionId, url },
            status: 'debug',
          })
        );
        setSecureValue(`${name}ID`, connectionPool[name]!.connectionId);
        dispatch(setConnectionID({ name: `${name}ID`, id: connectionPool[name]!.connectionId }));
        dispatch(setReconnecting({ name: `${name}Reconnecting`, value: false }));

        connectionPoolAlive[name] = setInterval(() => {
          const current = connectionPool[name];
          if (!current) {
            clearInterval(connectionPoolAlive[name]!);
            connectionPoolAlive[name] = null;
            dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
            dispatch(
              sendLog({
                title: buildSignalRConnectionLogTitle(name, 'Reconnecting'),
                data: { hub: name, url, reason: 'watchdog detected dropped connection' },
                status: 'warn',
              })
            );
            initConnection(config);
            return;
          }
          if (current.state === signalR.HubConnectionState.Disconnected) {
            clearInterval(connectionPoolAlive[name]!);
            connectionPoolAlive[name] = null;
            connectionPool[name] = null;
            dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
            dispatch(
              sendLog({
                title: buildSignalRConnectionLogTitle(name, 'Reconnecting'),
                data: { hub: name, url, state: current.state, reason: 'watchdog detected Disconnected state without onclose' },
                status: 'warn',
              })
            );
            initConnection(config);
          }
        }, 2000);

        if (callbacks?.[name]) {
          callbacks[name]!();
        }
      })
      .catch(async (error: Error) => {
        connectionStarting[name] = false;
        removeSecureValue(`${name}ID`);
        dispatch(setConnectionID({ name: `${name}ID`, id: null }));
        dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
        dispatch(
          addEventLogToHistory('SignalR:ConnectionError', {
            hub: name,
            error: error.message || error.toString(),
            timestamp: new Date().toISOString(),
          })
        );
        dispatch(
          sendLog({
            title: buildSignalRConnectionLogTitle(name, 'StartFailed'),
            data: { hub: name, url, error: error.message || error.toString(), retryInMs: 2000 },
            status: 'error',
          })
        );
        connectionPoolErrorAlive[name] = setTimeout(() => {
          connectionPool[name] = null;
          if (connectionPoolAlive[name]) {
            clearInterval(connectionPoolAlive[name]!);
            connectionPoolAlive[name] = null;
          }
          initConnection(config);
        }, 2000);
        console.log('SignalR connection error -', error);
      });
  } catch (e) {
    connectionStarting[name] = false;
    dispatch(setReconnecting({ name: `${name}Reconnecting`, value: true }));
    dispatch(
      sendLog({
        title: buildSignalRConnectionLogTitle(name, 'InitException'),
        data: { hub: name, url, error: e instanceof Error ? e.message || e.toString() : String(e), retryInMs: 2000 },
        status: 'error',
      })
    );
    if (connectionPoolErrorAlive[name]) {
      clearTimeout(connectionPoolErrorAlive[name]!);
    }
    connectionPoolErrorAlive[name] = setTimeout(() => {
      initConnection(config);
    }, 2000);
    console.log(e, 'Signal R error');
  }
};

export default createReducer<SignalRState>(initState, {
  [setConnectionID.type]: (state, action: { payload: SetConnectionIDPayload }) => ({
    ...state,
    [action.payload.name]: action.payload.id,
  }),
  [setReconnecting.type]: (state, action: { payload: SetReconnectingPayload }) => ({
    ...state,
    [action.payload.name]: action.payload.value,
  }),
  [clear.type]: () => {
    return initState;
  },
});
